import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  alCerrarseLaSesion,
  api,
  enviar,
  guardarSesion,
  restaurarSesion,
} from '../api/cliente';
import type { ClinicaDeSesion, RespuestaSesion } from '../api/tipos';

type Usuario = {
  usuarioId: string;
  nombreCompleto: string;
  clinicaId: string;
  clinica: string;
  rol: string;
  permisos: Set<string>;
  clinicas: ClinicaDeSesion[];
};

type ContextoSesion = {
  usuario: Usuario | null;
  cargando: boolean;
  ingresar: (email: string, password: string) => Promise<void>;
  salir: () => Promise<void>;
  cambiarDeClinica: (clinicaId: string) => Promise<void>;
  /** Verdadero si el usuario tiene el permiso. El servidor lo vuelve a verificar siempre. */
  puede: (permiso: string) => boolean;
};

const Contexto = createContext<ContextoSesion | null>(null);

const CLAVE_USUARIO = 'advanceodonto.usuario';

function aUsuario(respuesta: RespuestaSesion): Usuario {
  const clinica = respuesta.clinicas.find((c) => c.id === respuesta.clinicaId);

  return {
    usuarioId: respuesta.usuarioId,
    nombreCompleto: respuesta.nombreCompleto,
    clinicaId: respuesta.clinicaId,
    clinica: clinica?.nombre ?? '',
    rol: respuesta.rol,
    permisos: new Set(respuesta.permisos),
    clinicas: respuesta.clinicas,
  };
}

function recordar(respuesta: RespuestaSesion) {
  guardarSesion({
    tokenAcceso: respuesta.tokenAcceso,
    refreshToken: respuesta.refreshToken,
    expiraEn: respuesta.expiraEn,
  });

  try {
    localStorage.setItem(CLAVE_USUARIO, JSON.stringify(respuesta));
  } catch {
    // Sin almacenamiento la sesión dura lo que dura la pestaña.
  }
}

function usuarioRecordado(): Usuario | null {
  try {
    const crudo = localStorage.getItem(CLAVE_USUARIO);

    return crudo ? aUsuario(JSON.parse(crudo) as RespuestaSesion) : null;
  } catch {
    return null;
  }
}

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    // Si hay token guardado se reanuda la sesión sin volver a pedir la contraseña; el
    // primer 401 la cierra sola.
    const guardada = restaurarSesion();
    setUsuario(guardada ? usuarioRecordado() : null);
    setCargando(false);

    alCerrarseLaSesion(() => {
      setUsuario(null);

      try {
        localStorage.removeItem(CLAVE_USUARIO);
      } catch {
        // Nada que limpiar.
      }
    });
  }, []);

  const ingresar = useCallback(async (email: string, password: string) => {
    const respuesta = await api<RespuestaSesion>('/api/v1/auth/login', {
      metodo: 'POST',
      cuerpo: { email, password },
      anonimo: true,
    });

    recordar(respuesta);
    setUsuario(aUsuario(respuesta));
  }, []);

  const salir = useCallback(async () => {
    const guardada = restaurarSesion();

    try {
      if (guardada?.refreshToken) {
        await api('/api/v1/auth/logout', {
          metodo: 'POST',
          cuerpo: { refreshToken: guardada.refreshToken },
          anonimo: true,
        });
      }
    } catch {
      // Si el servidor no contesta igual se cierra del lado del navegador.
    }

    guardarSesion(null);

    try {
      localStorage.removeItem(CLAVE_USUARIO);
    } catch {
      // Nada que limpiar.
    }

    setUsuario(null);
  }, []);

  const cambiarDeClinica = useCallback(async (clinicaId: string) => {
    const guardada = restaurarSesion();

    if (!guardada?.refreshToken) {
      return;
    }

    // Cambiar de clínica reemite el token: el `clinica_id` vive dentro y nunca en la URL.
    const respuesta = await enviar<RespuestaSesion>('/api/v1/auth/refresh', {
      refreshToken: guardada.refreshToken,
      clinicaId,
    });

    recordar(respuesta);
    setUsuario(aUsuario(respuesta));
  }, []);

  const valor = useMemo<ContextoSesion>(
    () => ({
      usuario,
      cargando,
      ingresar,
      salir,
      cambiarDeClinica,
      puede: (permiso: string) => usuario?.permisos.has(permiso) ?? false,
    }),
    [usuario, cargando, ingresar, salir, cambiarDeClinica],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): ContextoSesion {
  const contexto = useContext(Contexto);

  if (!contexto) {
    throw new Error('useSesion se usa dentro de <ProveedorSesion>.');
  }

  return contexto;
}
